import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Card,
  CardHeader,
  CardBody,
  Row,
  Col,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import axios from "../../config/axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./advertise.css";

const MetaAdvertiseProduct = () => {
  const { productId } = useParams();
  const history = useHistory();
  const { user, token: authToken } = useSelector((state) => state.auth);
  const currentUserId = (user && (user._id || user.id)) || "";
  // Some Input events (especially paste/date/select) can be inconsistent.
  // Keep this resilient so typing always updates state.
  const evValue = (e) => {
    if (typeof e === "string" || typeof e === "number") return e;
    if (!e) return undefined;
    const v =
      e?.target?.value !== undefined
        ? e.target.value
        : e?.currentTarget?.value !== undefined
        ? e.currentTarget.value
        : e?.value;
    return v;
  };
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adAccounts, setAdAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [catalogs, setCatalogs] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [syncingProduct, setSyncingProduct] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [creatingCatalog, setCreatingCatalog] = useState(false);
  const [newCatalog, setNewCatalog] = useState({
    businessId: "",
    name: "",
    vertical: "commerce",
  });
  const [metaProductId, setMetaProductId] = useState("");
  const [form, setForm] = useState({
    name: "",
    dailyBudget: 10,
    ageMin: 18,
    ageMax: 65,
    catalogLandingUrl: "",
    startDate: "",
    endDate: "",
  });

  const headers = () => ({ Authorization: `Bearer ${authToken}` });

  useEffect(() => {
    if (!productId || !authToken || !currentUserId) return;
    axios
      .get(`/product/get/${productId}`, { headers: headers() })
      .then((res) => {
        const data = res.data.data;
        if (!data) {
          setProduct(null);
          return;
        }
        const sellerId = data.sellerId && (data.sellerId._id || data.sellerId);
        if (sellerId && String(sellerId) !== String(currentUserId)) {
          toast.error("You do not have access to this product");
          setProduct(null);
          return;
        }
        setProduct(data);
        const defaultLandingUrl =
          data.productUrl || `${window.location.origin}/productdetail/${data._id}`;
        setForm((f) => ({
          ...f,
          name: `Campaign - ${(data.name || "").slice(0, 40)}`,
          catalogLandingUrl: defaultLandingUrl,
        }));
      })
      .catch(() => {
        toast.error("Product not found");
        setProduct(null);
      })
      .finally(() => setLoading(false));
  }, [productId, authToken, currentUserId]);

  useEffect(() => {
    if (!authToken) return;
    axios
      .get("/ad-accounts", { headers: headers() })
      .then((res) => {
        const list = res.data.data || [];
        setAdAccounts(list);
        if (list.length > 0 && !selectedAccount) setSelectedAccount(list[0]);
      })
      .catch(() => setAdAccounts([]));
  }, [authToken]);

  useEffect(() => {
    if (!selectedAccount || !selectedAccount.id) return;
    setCatalogLoading(true);
    axios
      .get(`/ad-accounts/${selectedAccount.id}/catalogs`, { headers: headers() })
      .then((res) => {
        setCatalogs(res.data.data || []);
        const biz = res.data.businesses || [];
        setBusinesses(biz);
        if (!newCatalog.businessId && biz.length > 0) {
          setNewCatalog((p) => ({ ...p, businessId: biz[0].id }));
        }
      })
      .catch(() => setCatalogs([]))
      .finally(() => setCatalogLoading(false));
  }, [selectedAccount, authToken]);

  const hasCatalog = selectedAccount && selectedAccount.metaCatalogId;
  const catalogList = catalogs;

  const getRetailerId = (p) => {
    const base = (p && (p.displayId || p._id)) ? String(p.displayId || p._id) : "";
    if (!base) return "";
    return base.startsWith("prod_") ? base : `prod_${base}`;
  };

  const getProductLocations = () => {
    // Reuse product-level geo when available, otherwise fallback.
    const candidates = [
      product?.country,
      product?.shippingCountry,
      product?.location?.country,
      product?.residencyData?.country,
    ].filter(Boolean);
    if (candidates.length === 0) return ["US"];
    return [...new Set(candidates.map((c) => String(c).trim().toUpperCase()))];
  };

  const connectMeta = () => {
    axios
      .get("/auth/meta-ads/connect", { headers: headers() })
      .then((res) => {
        if (res.data && res.data.url) window.location.href = res.data.url;
        else toast.error("Could not start Meta connect");
      })
      .catch(() => toast.error("Connect failed"));
  };

  const selectCatalog = (catalogId) => {
    if (!selectedAccount) return;
    axios
      .patch(`/ad-accounts/${selectedAccount.id}/catalog`, { catalog_id: catalogId }, { headers: headers() })
      .then(() => {
        setSelectedAccount((a) => (a ? { ...a, metaCatalogId: catalogId } : a));
        toast.success("Catalog selected");
      })
      .catch((e) => toast.error(e.response?.data?.message || "Failed to set catalog"));
  };

  const createCatalog = (e) => {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    if (!selectedAccount) return;

    const formEl = e && e.currentTarget ? e.currentTarget : null;
    const elements = formEl && formEl.elements ? formEl.elements : {};
    const businessId = elements.businessId?.value || newCatalog.businessId;
    const name = elements.catalogName?.value || newCatalog.name;
    const vertical = elements.vertical?.value || newCatalog.vertical;

    if (!businessId || !name) {
      toast.error("Business and catalog name are required");
      return;
    }
    setCreatingCatalog(true);
    axios
      .post(
        `/ad-accounts/${selectedAccount.id}/catalogs`,
        {
          business_id: businessId,
          name,
          vertical,
        },
        { headers: headers() }
      )
      .then((res) => {
        const createdId = res.data?.data?.id;
        toast.success("Catalog created");
        // refresh catalogs then select it
        return axios.get(`/ad-accounts/${selectedAccount.id}/catalogs`, { headers: headers() }).then((r) => {
          setCatalogs(r.data.data || []);
          setBusinesses(r.data.businesses || []);
          if (createdId) selectCatalog(createdId);
          setNewCatalog((p) => ({ ...p, name: "" }));
        });
      })
      .catch((e) => toast.error(e.response?.data?.message || "Failed to create catalog"))
      .finally(() => setCreatingCatalog(false));
  };

  const ensureProductInCatalog = () => {
    if (!selectedAccount || !selectedAccount.metaCatalogId || !product) return;
    setSyncingProduct(true);
    const retailerId = getRetailerId(product);
    const legacyIds = Array.from(
      new Set(
        [
          product._id,
          product.displayId,
          `prod_${product._id}`,
          product.displayId ? `prod_${product.displayId}` : "",
        ]
          .map((x) => String(x || "").trim())
          .filter(Boolean)
      )
    );
    const payload = {
      catalog_id: selectedAccount.metaCatalogId,
      upsert: true,
      legacy_retailer_ids: legacyIds,
      products: [
        {
          retailer_id: retailerId,
          name: product.name || "",
          description: (product.description || "").slice(0, 5000),
          url: product.productUrl || window.location.origin + "/productdetail/" + product._id,
          image_url: (product.images && product.images[0]) || "",
          price: product.salesPrice != null ? product.salesPrice : product.regularPrice,
          brand: product.brand || "",
        },
      ],
    };
    axios
      .post(`/ad-accounts/${selectedAccount.id}/catalogs/products`, payload, { headers: headers() })
      .then(async (res) => {
        // Surface Meta batch errors (items_batch can return 200 with per-item failures).
        const responses = res?.data?.data || [];
        const handles = res?.data?.raw?.handles || [];
        const errs = (responses || [])
          .map((r) => r?.body?.error?.message || r?.body?.error || null)
          .filter(Boolean);
        if (errs.length) {
          toast.error(errs[0]);
          return;
        }

        if (handles && handles.length > 0) {
          toast.info("Sync started. Meta is processing your catalog update...");
          // Poll Meta batch status until finished (or timeout), then refresh products.
          const handle = handles[0];
          const startedAt = Date.now();
          const timeoutMs = 45000;
          let status = "unknown";
          let finishedWithErrors = false;
          while (Date.now() - startedAt < timeoutMs) {
            try {
              const s = await axios.get(
                `/ad-accounts/${selectedAccount.id}/catalogs/batch-status?catalog_id=${encodeURIComponent(
                  selectedAccount.metaCatalogId
                )}&handle=${encodeURIComponent(handle)}`,
                { headers: headers() }
              );
              const item = (s.data?.data || [])[0] || {};
              status = item.status || status;
              if (status === "finished") {
                const errorsCount = Number(item.errors_total_count || 0);
                finishedWithErrors = errorsCount > 0;
                break;
              }
            } catch (_) {}
            // wait 2s
            // eslint-disable-next-line no-await-in-loop
            await new Promise((r) => setTimeout(r, 2000));
          }

          if (status !== "finished") {
            toast.info("Sync queued. Please refresh in a few seconds to see the product in the catalog.");
          } else if (finishedWithErrors) {
            toast.error("Meta sync finished with errors. Please try syncing again.");
          } else {
            toast.success("Product synced to catalog");
          }
        } else if (!responses || responses.length === 0) {
          // No handles and no per-item responses — treat as unknown.
          toast.error("Meta did not return batch results. Please try syncing again.");
          return;
        } else {
          toast.success("Product synced to catalog");
        }
        // Try to resolve Meta product id for more reliable product_set filtering later.
        try {
          const r = await axios.get(
            `/ad-accounts/${selectedAccount.id}/catalogs/products?catalog_id=${encodeURIComponent(selectedAccount.metaCatalogId)}`,
            { headers: headers() }
          );
          const list = r.data?.data || [];
          const match = list.find((x) => String(x.retailer_id) === String(retailerId));
          setMetaProductId(match?.id || "");
        } catch (_) {
          // non-fatal; retailer_id filter still works
          setMetaProductId("");
        }
      })
      .catch((e) => toast.error(e.response?.data?.message || "Sync failed"))
      .finally(() => setSyncingProduct(false));
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const formEl = e.currentTarget;
    const elements = formEl && formEl.elements ? formEl.elements : {};
    const getElValue = (key) => (elements[key] && elements[key].value !== undefined ? elements[key].value : undefined);

    const campaignName = getElValue("campaignName");
    const dailyBudget = getElValue("dailyBudget");
    const ageMin = getElValue("ageMin");
    const ageMax = getElValue("ageMax");
    const landingUrl = getElValue("landingUrl");
    const startDate = getElValue("startDate");
    const endDate = getElValue("endDate");

    const payloadForm = {
      name: campaignName !== undefined ? campaignName : form.name,
      dailyBudget: dailyBudget !== undefined ? dailyBudget : form.dailyBudget,
      ageMin: ageMin !== undefined ? ageMin : form.ageMin,
      ageMax: ageMax !== undefined ? ageMax : form.ageMax,
      catalogLandingUrl: landingUrl !== undefined ? landingUrl : form.catalogLandingUrl,
      startDate: startDate !== undefined ? startDate : form.startDate,
      endDate: endDate !== undefined ? endDate : form.endDate,
    };
    if (!selectedAccount || !selectedAccount.metaCatalogId || !product) {
      toast.error("Select a catalog and ensure product is synced");
      return;
    }
    setSubmitting(true);
    const retailerId = getRetailerId(product);
    const payload = {
      name: payloadForm.name,
      metaCatalogId: selectedAccount.metaCatalogId,
      metaProductRetailerId: retailerId,
      ...(metaProductId ? { metaProductId } : {}),
      localProductId: product._id,
      budget: { daily: Number(payloadForm.dailyBudget) },
      targetAudience: {
        ageMin: Number(payloadForm.ageMin),
        ageMax: Number(payloadForm.ageMax),
        locations: getProductLocations(),
      },
      catalogLandingUrl:
        payloadForm.catalogLandingUrl ||
        product.productUrl ||
        `${window.location.origin}/productdetail/${product._id}`,
      startDate: payloadForm.startDate || undefined,
      endDate: payloadForm.endDate || undefined,
    };
    axios
      .post(`/ad-accounts/${selectedAccount.id}/campaigns`, payload, { headers: headers() })
      .then(() => {
        toast.success("Campaign created");
        history.push("/seller/social-advertisement");
      })
      .catch((e) => toast.error(e.response?.data?.message || "Campaign creation failed"))
      .finally(() => setSubmitting(false));
  };

  if (loading) {
    return (
      <div className="animated fadeIn p-4 text-center">
        <i className="fas fa-spinner fa-pulse fa-2x" style={{ color: "var(--primary)" }} aria-hidden />
        <p className="mt-2">Loading product...</p>
      </div>
    );
  }
  if (!currentUserId) {
    return (
      <div className="animated fadeIn p-4">
        <Card>
          <CardBody>
            <p>Please log in to advertise products on Meta.</p>
            <Button color="primary" onClick={() => history.push("/auth/login")}>
              Log in
            </Button>
          </CardBody>
        </Card>
      </div>
    );
  }
  if (!product) {
    return (
      <div className="animated fadeIn p-4">
        <Card>
          <CardBody>
            <p>Product not found or you do not have access.</p>
            <Button color="primary" onClick={() => history.push("/seller/products")}>
              Back to Products
            </Button>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="animated fadeIn">
      <ToastContainer autoClose={5000} />
      <Row>
        <Col>
          <Card>
            <CardHeader>
              <b>Advertise on Meta — {product.name}</b>
            </CardHeader>
            <CardBody>
              {adAccounts.length === 0 ? (
                <div>
                  <p>Connect your Meta (Facebook) account to run ads for this product.</p>
                  <Button color="primary" onClick={connectMeta}>
                    Connect Meta account
                  </Button>
                </div>
              ) : (
                <>
                  <FormGroup>
                    <Label>Ad account</Label>
                    <Input
                      type="select"
                      value={selectedAccount ? selectedAccount.id : ""}
                      onChange={(e) => {
                        const id = evValue(e);
                        setSelectedAccount(adAccounts.find((a) => a.id === id) || null);
                      }}
                    >
                      {adAccounts.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.accountName || a.accountId}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>

                  <FormGroup>
                    <Label>Select catalog</Label>
                    {catalogLoading ? (
                      <i className="fas fa-spinner fa-pulse" aria-hidden />
                    ) : catalogList.length === 0 ? (
                      <p className="text-muted">No product catalogs found. Create one below (or in Meta Business Manager).</p>
                    ) : (
                      <Input
                        type="select"
                        value={selectedAccount?.metaCatalogId || ""}
                        onChange={(e) => selectCatalog(evValue(e))}
                      >
                        <option value="">— Select catalog —</option>
                        {catalogList.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </Input>
                    )}
                  </FormGroup>

                  {!hasCatalog && (
                    <Card className="mt-3">
                      <CardHeader>
                        <b>Create a new Meta catalog</b>
                      </CardHeader>
                      <CardBody>
                        <Form onSubmit={createCatalog}>
                        <Row form>
                          <Col md={6}>
                            <FormGroup>
                              <Label>Business</Label>
                              <Input
                                type="select"
                                name="businessId"
                                defaultValue={newCatalog.businessId}
                              >
                                {(businesses || []).map((b) => (
                                  <option key={b.id} value={b.id}>
                                    {b.name} ({b.id})
                                  </option>
                                ))}
                              </Input>
                              {(!businesses || businesses.length === 0) && (
                                <small className="text-muted">
                                  No businesses found for this Meta user. Add a Business in Meta Business Manager.
                                </small>
                              )}
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup>
                              <Label>Catalog name</Label>
                              <Input
                                name="catalogName"
                                defaultValue={newCatalog.name}
                                onChange={(e) => {
                                  const v = evValue(e);
                                  setNewCatalog((p) => ({ ...p, name: v !== undefined ? v : p.name }));
                                }}
                                placeholder="My Product Catalog"
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        <FormGroup>
                          <Label>Vertical</Label>
                          <Input
                            type="select"
                            name="vertical"
                            defaultValue={newCatalog.vertical}
                          >
                            <option value="commerce">commerce</option>
                            <option value="offline_commerce">offline_commerce</option>
                            <option value="transactable_items">transactable_items</option>
                            <option value="vehicle_offers">vehicle_offers</option>
                            <option value="jobs">jobs</option>
                            <option value="real_estate">home_listings</option>
                            <option value="travel">destinations</option>
                          </Input>
                        </FormGroup>
                        <Button type="submit" color="primary" disabled={creatingCatalog}>
                          {creatingCatalog ? "Creating..." : "Create catalog"}
                        </Button>
                        </Form>
                      </CardBody>
                    </Card>
                  )}

                  {hasCatalog && (
                    <>
                      <FormGroup>
                        <Button color="secondary" size="sm" onClick={ensureProductInCatalog} disabled={syncingProduct}>
                          {syncingProduct ? "Syncing..." : "Sync this product to catalog"}
                        </Button>
                      </FormGroup>

                      <Form onSubmit={onSubmit}>
                        <FormGroup>
                          <Label>Campaign name</Label>
                          <Input
                            name="campaignName"
                            defaultValue={form.name}
                            onChange={(e) => {
                              const v = evValue(e);
                              setForm((f) => ({ ...f, name: v !== undefined ? v : f.name }));
                            }}
                            required
                          />
                        </FormGroup>
                        <FormGroup>
                          <Label>Daily budget (USD)</Label>
                          <Input
                            type="number"
                            min={1}
                            step={1}
                            name="dailyBudget"
                            defaultValue={form.dailyBudget}
                            onChange={(e) => {
                              const v = evValue(e);
                              setForm((f) => ({ ...f, dailyBudget: v !== undefined ? v : f.dailyBudget }));
                            }}
                          />
                        </FormGroup>
                        <Row form>
                          <Col md={6}>
                            <FormGroup>
                              <Label>Age min</Label>
                              <Input
                                type="number"
                                min={18}
                                max={65}
                                name="ageMin"
                                defaultValue={form.ageMin}
                                onChange={(e) => {
                                  const v = evValue(e);
                                  setForm((f) => ({ ...f, ageMin: v !== undefined ? v : f.ageMin }));
                                }}
                              />
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup>
                              <Label>Age max</Label>
                              <Input
                                type="number"
                                min={18}
                                max={65}
                                name="ageMax"
                                defaultValue={form.ageMax}
                                onChange={(e) => {
                                  const v = evValue(e);
                                  setForm((f) => ({ ...f, ageMax: v !== undefined ? v : f.ageMax }));
                                }}
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        <FormGroup>
                          <Label>Landing URL (optional)</Label>
                          <Input
                            type="url"
                            name="landingUrl"
                            defaultValue={form.catalogLandingUrl}
                            onChange={(e) => {
                              const v = evValue(e);
                              setForm((f) => ({ ...f, catalogLandingUrl: v !== undefined ? v : f.catalogLandingUrl }));
                            }}
                            placeholder={product.productUrl || "Product page URL"}
                          />
                        </FormGroup>
                        <Row form>
                          <Col md={6}>
                            <FormGroup>
                              <Label>Start date (optional)</Label>
                              <Input
                                type="date"
                                name="startDate"
                                defaultValue={form.startDate}
                                onChange={(e) => {
                                  const v = evValue(e);
                                  setForm((f) => ({ ...f, startDate: v !== undefined ? v : f.startDate }));
                                }}
                              />
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup>
                              <Label>End date (optional)</Label>
                              <Input
                                type="date"
                                name="endDate"
                                defaultValue={form.endDate}
                                onChange={(e) => {
                                  const v = evValue(e);
                                  setForm((f) => ({ ...f, endDate: v !== undefined ? v : f.endDate }));
                                }}
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        <Button type="submit" color="primary" disabled={submitting}>
                          {submitting ? "Creating..." : "Create Meta campaign"}
                        </Button>
                        <Button color="secondary" className="ml-2" onClick={() => history.push("/seller/products")}>
                          Cancel
                        </Button>
                      </Form>
                    </>
                  )}
                </>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default MetaAdvertiseProduct;
